import { HttpBackend, provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  APP_INITIALIZER,
  EnvironmentProviders,
  importProvidersFrom,
  inject,
  LOCALE_ID,
  ModuleWithProviders,
  makeEnvironmentProviders,
  Provider
} from '@angular/core';
import { provideApiClient } from '@eng-dx-gpt/api-client';
import { provideLoading } from '@eng-dx-gpt/loading';
import { provideLogger } from '@eng-dx-gpt/logger';
import { SHARED_APP_CONFIG_TOKEN } from '@eng-dx-gpt/theme';
import { NbRoleProvider, NbSecurityModule } from '@nebular/security';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { MultiTranslateHttpLoader } from 'ngx-translate-multi-http-loader';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { RUNTIME_APP_CONFIG } from './../app.config';
import { acl, RoleProvider, UserService } from './auth';
import { AUTH_INTERCEPTOR_HEADER, authInterceptor, errorInterceptor, loggerInterceptor } from './interceptors';
import { apiInterceptor } from './interceptors/api.interceptor';
import { httpLoaderInterceptor } from './interceptors/http-loader.interceptor';
import { AnalyticsService, PageTitleService, SeoService } from './services';
import { INTERNAL_APP_CONFIG } from './token';

const coreServices = [AnalyticsService, PageTitleService, SeoService, UserService, MessageService];
const coreProvideValues = (appConfig: typeof RUNTIME_APP_CONFIG) => [
  { provide: AUTH_INTERCEPTOR_HEADER, useValue: 'access-token' },
  { provide: INTERNAL_APP_CONFIG, useValue: appConfig },
  {
    provide: SHARED_APP_CONFIG_TOKEN,
    useValue: {
      frontend: {
        appBasePath: appConfig?.frontend?.appBasePath
      },
      knowledgeBase: {
        maxLength: appConfig?.knowledgeBase?.maxLength
      },
      backend: {
        baseUrl: appConfig?.backend?.baseUrl
      }
    }
  },
  { provide: LOCALE_ID, useValue: 'it' },
  {
    provide: NbRoleProvider,
    useClass: RoleProvider
  }
];
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const legacyProviders: (appConfig: typeof RUNTIME_APP_CONFIG) => ModuleWithProviders<unknown>[] = (appConfig) => [
  NbSecurityModule.forRoot(acl)
];

const provideTranslation: (config?: typeof RUNTIME_APP_CONFIG) => EnvironmentProviders = (
  config?: typeof RUNTIME_APP_CONFIG
) => {
  const providers: Array<EnvironmentProviders | Provider> = [];

  // Create a dynamic translation loader that uses the global path set by ThemeManager
  const getDynamicTranslationPath = () => {
    const globalPath = (window as Window & typeof globalThis & { __TRANSLATIONS_BASE_PATH__?: string })
      .__TRANSLATIONS_BASE_PATH__;
    if (globalPath) {
      return globalPath;
    }

    // Fallback to the old logic if global path is not available
    let assetBasePath = '';
    if (config?.frontend.appBasePath) {
      assetBasePath = config.frontend.appBasePath.endsWith('/')
        ? config.frontend.appBasePath.substring(0, config.frontend.appBasePath.lastIndexOf('/'))
        : config.frontend.appBasePath;
    }
    return `${assetBasePath}/i18n/`;
  };

  // Add provider to initialize the TranslateModule
  const translateLoader = () => ({
    provide: TranslateLoader,
    useFactory: () => {
      const basePath = getDynamicTranslationPath();
      return new MultiTranslateHttpLoader(inject(HttpBackend), [
        { prefix: basePath, suffix: '.json' },
        { prefix: './i18n/', suffix: '.json' }
      ]);
    },
    deps: [HttpBackend]
  });

  const defaultLang = config?.i18n?.defaultLang || 'it';
  const availableLanguages = config?.i18n?.availableLanguages || ['it'];
  providers.push(
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: translateLoader(),
        defaultLanguage: defaultLang,
        useDefaultLang: true
      })
    )
  );

  // Add provider to initialize library default languages
  providers.push({
    provide: APP_INITIALIZER,
    useFactory: (translateService: TranslateService) => {
      return () => {
        translateService.addLangs(availableLanguages);
      };
    },
    multi: true,
    deps: [TranslateService]
  });

  return makeEnvironmentProviders(providers);
};

export const provideCore: (appConfig: typeof RUNTIME_APP_CONFIG) => EnvironmentProviders = (appConfig) => {
  const providers: Array<EnvironmentProviders | Provider> = [
    ...coreProvideValues(appConfig),
    importProvidersFrom(...legacyProviders(appConfig)),
    importProvidersFrom(ToastModule),

    provideApiClient(appConfig.backend),

    provideLogger(appConfig.logger),
    provideLoading(appConfig.loading),
    provideTranslation(appConfig),
    provideHttpClient(
      withInterceptors([apiInterceptor, authInterceptor, errorInterceptor, loggerInterceptor, httpLoaderInterceptor])
    ),

    ...coreServices
  ];

  return makeEnvironmentProviders(providers);
};
